import { z } from 'zod';
import { isoDate, name, richText, uuid } from './common.js';
// ───────────────────────────── Objetivos (spec §3.7)
export const goalLevelSchema = z.object({ name: name(80) });
export const goalSchema = z.object({
    title: name(300),
    description: richText.optional(),
    parentId: uuid.nullish(),
    levelId: uuid.nullish(),
    ownerId: uuid.nullish(),
    startDate: isoDate.nullish(),
    dueDate: isoDate.nullish(),
    weight: z.number().min(0).max(1000).nullish(),
    code: z.string().max(40).nullish(),
});
export const indicatorSchema = z.object({
    name: name(200),
    unit: z.string().max(40).default(''),
    baseline: z.number(),
    target: z.number(),
    /** El avance del objetivo sale de este indicador en vez de los vínculos. */
    drivesProgress: z.boolean().default(false),
});
export const measurementSchema = z.object({ value: z.number(), date: isoDate, note: z.string().max(500).optional() });
export const goalLinkSchema = z.object({ projectId: uuid.optional(), taskId: uuid.optional(), weight: z.number().min(0).max(1000).nullish() }).refine((v) => !!v.projectId !== !!v.taskId, 'Vincula un proyecto o una tarea');
// ───────────────────────────── Portafolios
export const portfolioSchema = z.object({ name: name(160), description: z.string().max(2000).optional(), projectIds: z.array(uuid).max(500).default([]) });
// ───────────────────────────── Colaboradores externos y entregables
export const engagementSchema = z.object({
    userId: uuid,
    title: name(200),
    description: richText.optional(),
    startDate: isoDate,
    endDate: isoDate,
    supervisorId: uuid,
    projectId: uuid.nullish(),
    /** Periodicidad del informe de actividades. */
    reportEvery: z.enum(['MONTHLY', 'BIWEEKLY', 'WEEKLY']).default('MONTHLY'),
}).refine((v) => v.endDate >= v.startDate, { message: 'La fecha de fin debe ser posterior al inicio', path: ['endDate'] });
export const deliverableSchema = z.object({ title: name(500), description: z.string().max(4000).optional() });
export const ActivityReportStatus = { DRAFT: 'DRAFT', SUBMITTED: 'SUBMITTED', APPROVED: 'APPROVED', RETURNED: 'RETURNED' };
export const ACTIVITY_REPORT_STATUS_LABEL = { DRAFT: 'Borrador', SUBMITTED: 'Enviado', APPROVED: 'Aprobado', RETURNED: 'Devuelto' };
export const activityReportCreateSchema = z.object({ periodFrom: isoDate, periodTo: isoDate, summary: z.string().max(10_000).optional() });
export const activityReportReviewSchema = z.object({ decision: z.enum(['APPROVE', 'RETURN']), comment: z.string().max(4000).optional() });
// ───────────────────────────── Acciones correctivas
export const correctivePlanSchema = z.object({ title: name(300), origin: z.string().max(200).optional(), description: richText.optional(), ownerId: uuid.nullish() });
export const FindingStatus = { OPEN: 'OPEN', IN_PROGRESS: 'IN_PROGRESS', VERIFYING: 'VERIFYING', CLOSED: 'CLOSED' };
export const FINDING_STATUS_LABEL = { OPEN: 'Abierto', IN_PROGRESS: 'En curso', VERIFYING: 'En verificación', CLOSED: 'Cerrado' };
export const findingSchema = z.object({
    code: z.string().max(40).nullish(),
    title: name(500),
    description: richText.optional(),
    cause: z.string().max(4000).optional(),
    status: z.nativeEnum(FindingStatus).optional(),
    ownerId: uuid.nullish(),
    dueDate: isoDate.nullish(),
});
export const correctiveActionSchema = z.object({ title: name(500), ownerId: uuid.nullish(), dueDate: isoDate.nullish(), projectId: uuid, requiresEvidence: z.boolean().default(true) });
// ───────────────────────────── Riesgos
export const riskSchema = z.object({
    title: name(500),
    description: z.string().max(4000).optional(),
    category: z.string().max(60).nullish(),
    process: z.string().max(200).nullish(),
    probability: z.number().int().min(1).max(5),
    impact: z.number().int().min(1).max(5),
    ownerId: uuid.nullish(),
});
export const riskControlSchema = z.object({ description: z.string().trim().min(1).max(2000), kind: z.enum(['PREVENTIVE', 'DETECTIVE', 'CORRECTIVE']) });
export const riskTreatmentSchema = z.object({ title: name(500), projectId: uuid, ownerId: uuid.nullish(), dueDate: isoDate.nullish() });
// ───────────────────────────── Reuniones y compromisos
export const meetingSchema = z.object({
    title: name(300),
    date: isoDate,
    kind: z.string().max(60).nullish(),
    attendeeIds: z.array(uuid).max(200).default([]),
    agenda: z.string().max(10_000).optional(),
    minutes: richText.optional(),
    projectId: uuid.nullish(),
});
export const commitmentSchema = z.object({ text: z.string().trim().min(1).max(1000), ownerId: uuid.nullish(), dueDate: isoDate.nullish(), projectId: uuid });
//# sourceMappingURL=management.js.map